import get from 'lodash.get';
import { Fragment, FunctionComponent } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay/legacy';
import {
    achFormUpdated,
    toggleBillingAddress,
    achFormUpdateBlurred,
} from '../actions/checkoutPaymentActions';
import { isVerifyingBankAccount } from '../reducers/checkoutPaymentReducer';
import styles from './styles/CheckoutACHForm.scss';

import { Checkbox } from 'dibs-elements/exports/Checkbox';
import ACHForm, { ACHFormValue, defaultFormValue, ACHBlurred } from 'dibs-ach-form/exports/ACHForm';
import achValidate from 'dibs-ach-form/exports/ACHValidate';
import { ControlledAddressForm } from 'dibs-address-form';
import CheckoutEoriVatForm from '../components/forms/CheckoutEoriVatForm';
import { CheckoutACHForm_viewer$data } from './__generated__/CheckoutACHForm_viewer.graphql';
import { CheckoutACHForm_buyer$data } from './__generated__/CheckoutACHForm_buyer.graphql';

type updateType = {
    field: string;
    value: string;
};

type achFormUpdateType = {
    field: string;
    value: string;
    isVerifying: boolean;
};

type Address = {
    city: string;
    country: string;
    firstName: string;
    lastName: string;
    line1: string;
    line2?: string;
    state: string;
    zipCode: string;
};

type Props = {
    viewer: CheckoutACHForm_viewer$data;
    buyer: CheckoutACHForm_buyer$data;
    isVerifying?: boolean;
    isPaymentSaving?: boolean;
    isBillingSameAsShipping: boolean;
    newACHForm?: ACHFormValue;
    onACHFormUpdated: (arg0: achFormUpdateType) => void;
    onToggleBillingAddress: (arg0: boolean) => void;
    onACHFormUpdateBlurred: (arg: ACHBlurred) => void;
    relay?: RelayProp;
    newBillingForm?: Address;
    onValidationUpdate?: () => void;
    onValidationError?: () => void;
    shippingAddressId?: string;
    achFormBlurred: ACHBlurred;
    shippingCountry?: string;
    verifyBankAccountForm: ACHFormValue;
    verifyBankAccountToken: string;
    achShowFailedSubmissionError: boolean;
};

const CheckoutACHForm: FunctionComponent<Props> = props => {
    const {
        isVerifying = false,
        isPaymentSaving = false,
        isBillingSameAsShipping,
        newACHForm = defaultFormValue,
        onACHFormUpdated,
        onToggleBillingAddress,
        onACHFormUpdateBlurred,
        relay = {},
        viewer,
        buyer,
        newBillingForm = {} as Address,
        shippingCountry,
        achFormBlurred,
        verifyBankAccountToken,
        verifyBankAccountForm,
        achShowFailedSubmissionError,
    } = props;

    const onToggle = (checked: boolean): void => onToggleBillingAddress(checked);

    const onUpdate = ({ field, value }: updateType): void =>
        onACHFormUpdated({ field, value, isVerifying });

    const getSelectedBankAccountLastFour = (): string => {
        const bankAccount =
            (buyer?.wallet?.paymentMethods || []).find(
                ({ paymentMethod }: $TSFixMe) => paymentMethod.token === verifyBankAccountToken
            ) || null;
        return get(bankAccount, 'paymentMethod.last4');
    };

    const foreignShippingCountry = shippingCountry !== 'US';
    const isTrade = !!buyer?.isTrade;
    const isBusinessInformation = newACHForm?.ownershipType === 'business';

    return (
        <Fragment>
            {isVerifying && (
                <div className={styles.container}>
                    <p className={styles.header}>
                        <FormattedMessage
                            id="checkout.CheckoutACHForm.verifyBankAccountHeader"
                            defaultMessage="Please verify your account"
                        />
                        <span className={styles.digits}>
                            <FormattedMessage
                                id="checkout.CheckoutACHForm.verifyCardDigits"
                                defaultMessage="ending in {lastDigits}"
                                values={{ lastDigits: getSelectedBankAccountLastFour() }}
                            />
                        </span>
                    </p>

                    <p className={styles.largeDisclaimer}>
                        <FormattedMessage
                            id="checkout.CheckoutACHForm.verifyCardText"
                            defaultMessage="For security purposes, we require you to re-enter your bank account number, name, and billing address for each new shipping address."
                        />
                    </p>
                </div>
            )}
            <ACHForm
                formValue={isVerifying ? verifyBankAccountForm : (newACHForm as ACHFormValue)}
                onChange={onUpdate}
                blurred={achFormBlurred}
                onBlurred={newBlurredObject => onACHFormUpdateBlurred(newBlurredObject)}
                errors={
                    isVerifying
                        ? get(achValidate(verifyBankAccountForm), 'fieldErrors')
                        : get(achValidate(newACHForm), 'fieldErrors')
                }
                disableForm={isPaymentSaving}
                disabledFields={isVerifying ? ['accountType', 'routingNumber'] : ['']}
                renderChildren={() => {
                    const showAddressForm =
                        !isBillingSameAsShipping || foreignShippingCountry || isVerifying;
                    return (
                        <div className={styles.achAddressForm}>
                            {!foreignShippingCountry && (
                                <Checkbox
                                    name="isBillingSameAsShipping"
                                    label={
                                        <FormattedMessage
                                            id="checkout.CheckoutACHForm.sameAsShipping"
                                            defaultMessage="Billing Address is the Same as Shipping Address"
                                        />
                                    }
                                    checked={isBillingSameAsShipping && !isVerifying}
                                    disabled={isVerifying || isPaymentSaving}
                                    wrapperClass={styles.checkbox}
                                    onChange={onToggle}
                                    dataTn="same-as-shipping-address-checkbox"
                                />
                            )}
                            {showAddressForm && (
                                <ControlledAddressForm
                                    relay={relay}
                                    viewer={viewer}
                                    address={newBillingForm}
                                    hideCompany
                                    hidePhoneNumber
                                    hideSubmit
                                    validations={{ allowPOBox: true }}
                                    disableForm={isPaymentSaving}
                                    onUpdate={onUpdate}
                                    showAllErrors={achShowFailedSubmissionError}
                                />
                            )}
                            {isTrade && isBusinessInformation && (
                                <CheckoutEoriVatForm buyer={buyer} hideEori />
                            )}
                        </div>
                    );
                }}
            />
        </Fragment>
    );
};

type connectTransactionType = {
    shippingAddressId: string;
};

type connectPaymentType = {
    newACHForm: ACHFormValue;
    newBillingForm: Address;
    isBillingSameAsShipping: boolean;
    defaultBlurred: ACHBlurred;
    achFormBlurred: ACHBlurred;
    isPaymentSaving: boolean;
    verifyBankAccountToken: string;
    verifyBankAccountForm: ACHFormValue;
    achShowFailedSubmissionError: boolean;
};

type connectShippingType = {
    defaultCountry: string;
};

type connectType = {
    transaction: connectTransactionType;
    payment: connectPaymentType;
    shipping: connectShippingType;
};

export const ConnectedCheckoutACHForm = connect(
    ({ transaction, payment, shipping }: connectType) => {
        const isVerifying = isVerifyingBankAccount(payment);
        return {
            achShowFailedSubmissionError: payment.achShowFailedSubmissionError,
            achFormBlurred: payment.achFormBlurred,
            defaultBlurred: payment.defaultBlurred,
            isPaymentSaving: payment.isPaymentSaving,
            shippingAddressId: transaction.shippingAddressId,
            shippingCountry: shipping.defaultCountry,
            newACHForm: payment.newACHForm,
            newBillingForm: payment.newBillingForm,
            isBillingSameAsShipping: payment.isBillingSameAsShipping,
            verifyBankAccountToken: payment.verifyBankAccountToken,
            verifyBankAccountForm: payment.verifyBankAccountForm,
            isVerifying,
        };
    },
    {
        onACHFormUpdateBlurred: achFormUpdateBlurred,
        onACHFormUpdated: achFormUpdated,
        onToggleBillingAddress: toggleBillingAddress,
    }
)(CheckoutACHForm);

export default createFragmentContainer(ConnectedCheckoutACHForm, {
    viewer: graphql`
        fragment CheckoutACHForm_viewer on Viewer {
            ...ControlledAddressForm_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutACHForm_buyer on User {
            ...CheckoutEoriVatForm_buyer
            isTrade: isVerifiedTrade
            wallet {
                paymentMethods {
                    paymentMethod {
                        ... on ACH {
                            token
                            last4
                            accountType
                            routingNumber
                        }
                    }
                }
            }
        }
    `,
});
